import type { FormAutocompleteOption } from "../components/ui/formAutocomplete/formAutocomplete";

/**
 * Функция для преобразования enum в опции с локализацией
 *
 * @param enumObject - TypeScript enum объект
 * @param i18nKeys - Мапинг значений enum на ключи локализации
 * @param t - Функция локализации
 * @param options - Дополнительные опции:
 *   - excludeValues: Массив значений, которые нужно исключить
 *   - includeValues: Массив значений, которые нужно включить (если указан, excludeValues игнорируется)
 * @returns Массив локализованных опций для FormAutocompleteOption
 *
 * @example
 * // Исключить UNSPECIFIED
 * enumToLocalizedAutocompleteOptions(Gender, GENDER_I18N_KEYS, t, {
 *   excludeValues: [Gender.UNSPECIFIED]
 * })
 *
 * @example
 * // Включить только определенные значения
 * enumToLocalizedAutocompleteOptions(Theme, THEME_I18N_KEYS, t, {
 *   includeValues: [Theme.LIGHT, Theme.DARK, Theme.UNSPECIFIED]
 * })
 */
export function enumToLocalizedAutocompleteOptions<T extends number>(
  enumObject: Record<string, string | T>,
  i18nKeys: Record<T, string>,
  t: (key: string) => string,
  options?: {
    excludeValues?: T[];
    includeValues?: T[];
  },
): FormAutocompleteOption[] {
  const { excludeValues = [], includeValues } = options || {};

  const values = Object.values(enumObject).filter(
    (value): value is T => typeof value === "number",
  );

  const filteredValues = includeValues
    ? values.filter((value) => includeValues.includes(value))
    : values.filter((value) => !excludeValues.includes(value));

  return filteredValues.map((value) => ({
    value,
    label: t(i18nKeys[value]),
  }));
}

/**
 * Получает все значения enum, исключая указанные
 *
 * @param enumObject - TypeScript enum объект
 * @param options - Дополнительные опции:
 *   - excludeValues: Массив значений, которые нужно исключить
 *   - includeValues: Массив значений, которые нужно включить (если указан, excludeValues игнорируется)
 * @returns Массив всех значений enum
 *
 * @example
 * // Получить все значения enum
 * getAllEnumValues(ConsumeType)
 *
 * @example
 * // Получить все типы контента кроме UNSPECIFIED
 * getAllEnumValues(ContentType, { excludeValues: [ContentType.UNSPECIFIED] })
 *
 * @example
 * // Получить только определенные значения
 * getAllEnumValues(ConsumeType, {
 *   includeValues: [ConsumeType.WATCHED, ConsumeType.WATCHING]
 * })
 */
export function getAllEnumValues<T extends number>(
  enumObject: Record<string, string | T>,
  options?: {
    excludeValues?: T[];
    includeValues?: T[];
  },
): T[] {
  const { excludeValues = [], includeValues } = options || {};

  const values = Object.values(enumObject).filter(
    (value): value is T => typeof value === "number",
  );

  return includeValues
    ? values.filter((value) => includeValues.includes(value))
    : values.filter((value) => !excludeValues.includes(value));
}

/**
 * Извлекает значения енума из побитовой маски
 * @param flags - побитовая маска
 * @param enumType - объект енума
 * @param excludeValue - значение, которое нужно исключить (например, UNSPECIFIED)
 * @returns массив значений енума
 */
export function extractEnumFlags<T extends number>(
  flags: number,
  enumType: Record<string, T | string>,
  excludeValue?: T,
): T[] {
  const result: T[] = [];

  for (const value of Object.values(enumType)) {
    if (
      typeof value === "number" &&
      (excludeValue === undefined || value !== excludeValue) &&
      (flags & value) !== 0
    ) {
      result.push(value);
    }
  }

  return result;
}

/**
 * Checks if a value is a valid enum value
 * @param value - The value to check
 * @param enumType - The enum object to check against
 * @returns true if the value exists in the enum
 *
 * @example
 * // Check if a number is a valid PositionType
 * if (isValidEnumValue(5, PositionType)) {
 *   // Safe to use as PositionType
 * }
 */
export function isValidEnumValue<T extends number>(
  value: unknown,
  enumType: Record<string, T | string>,
): value is T {
  if (typeof value !== "number") {
    return false;
  }

  return Object.values(enumType)
    .filter((v): v is T => typeof v === "number")
    .includes(value as T);
}
